/**
 * The window a process step's equipment is held for.
 *
 * **Mirrors `cgt-backend/src/process-steps/reservation-window.ts` and must stay
 * in step with it.** The backend is the authority — it is what actually books —
 * but the modal asks about availability BEFORE booking, and it has to ask about
 * the same window it is going to request. Compute the window differently here
 * and the preview says a unit is free at a time the booking then refuses, which
 * reads as the app contradicting itself.
 *
 * Pure and dependency-free, like `src/lib/scheduling/engine.ts`, so it is unit
 * testable — vitest here is node-only and matches `src/**\/*.test.ts`.
 */

const MS_PER_HOUR = 3_600_000

/**
 * One equipment requirement's configured per-run timing.
 *
 * `prepHrs` is optional because rows created before PREP existed carry no value
 * of their own; absent means zero, never an Invalid Date window.
 */
export interface RequirementHours {
  prepHrs?: number
  setupHrs: number
  runHrs: number
  unloadHrs: number
}

/** Setup + run + unload. The part of the window the unit is actually in use. */
export function runHoursFor(hours: RequirementHours): number {
  return hours.setupHrs + hours.runHrs + hours.unloadHrs
}

/**
 * The window one equipment requirement will be held for, or null when the
 * requirement configures no run time — there is nothing to hold, and the
 * backend refuses that booking with `no_run_time`.
 *
 * `expectedStartIso` is the step's single planned start; the requirement's own
 * `prepHrs` delays the hold from there, so two units on the same step can be
 * wanted at different times. Prep moves the window, it never lengthens it, and
 * prep alone never creates one. An unparseable or empty start yields null.
 */
export function reservationWindow(
  expectedStartIso: string,
  hours: RequirementHours,
): { from: string; to: string } | null {
  const total = runHoursFor(hours)
  if (!(total > 0)) return null
  const base = new Date(expectedStartIso).getTime()
  if (Number.isNaN(base)) return null
  const from = base + Math.max(hours.prepHrs ?? 0, 0) * MS_PER_HOUR
  return {
    from: new Date(from).toISOString(),
    to: new Date(from + total * MS_PER_HOUR).toISOString(),
  }
}
